import { createContext, useContext, type ReactNode } from 'react'
import { tokens, type DesignTokens } from './tokens'

const ThemeContext = createContext<DesignTokens>(tokens)

/**
 * Exposes `tokens.ts` to components that need a token as a value — the typed-theme half of
 * P6 §1. It writes nothing to the DOM, deliberately:
 *
 * - The CSS custom properties are a static `:root` block in `global.css` (kept equal to
 *   `cssVariables(tokens)` by `theme.test.tsx`). Injecting them from an effect meant the first
 *   paint had no palette at all — a white frame at exactly the edges the shell works to keep
 *   cream.
 * - `<meta name="theme-color">` is `index.html`'s, and it is `--g0`. This component used to
 *   overwrite it with `color.surface.frame` (#2b1d16, the *desktop bezel*), which is what
 *   tinted the phone's browser chrome dark brown against a cream app.
 *
 * D78's Heebo is self-hosted from `public/fonts/` — see `global.css`'s `@font-face` rules.
 */
export function ThemeProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  return <ThemeContext.Provider value={tokens}>{children}</ThemeContext.Provider>
}

export function useTheme(): DesignTokens {
  return useContext(ThemeContext)
}
